import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $initialCurrenciesAvailableStore, $monthlyTransactionStore } from '@lib/store';
import { cn, DateUtils, PaletteUtils } from '@shared/utils';
import { TransactionDirection } from '@shared/api';
import { TransactionItem } from './transactionItem';
import { View } from '@widgets';
import { Select, Tabs, Text } from '@shared/widgets';

const CURRENT_MONTH_INDEX = dayjs().month() + 1;
const CURRENT_YEAR = dayjs().year();

const Tab = {
  Outgoing: 'outgoing',
  Incoming: 'incoming',
} as const;
type Tab = (typeof Tab)[keyof typeof Tab];

interface MonthlyTransactionProps {
  minimalYear: number;
  minimalMonth: number;
}

export const MonthlyTransaction = (props: MonthlyTransactionProps) => {
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const payment = useUnit($monthlyTransactionStore.store);

  const { t, i18n } = useTranslation('dashboard');

  const [tab, setTab] = useState<Tab>(Tab.Outgoing);
  const [currency, setCurrency] = useState<string>(currenciesAvailableStore.data[0]);
  const yearsList = useMemo(() => DateUtils.getYearsList(props.minimalYear), [props.minimalYear]);
  const [selectedYear, setSelectedYear] = useState<string>(yearsList[yearsList.length - 1]);
  const currentMonth = DateUtils.getCurrentMonth({ locale: i18n.language });
  const monthsList = useMemo(
    () =>
      DateUtils.getMonthsSelectList({
        locale: i18n.language,
        minimalMonth: +selectedYear! === props.minimalYear ? props.minimalMonth : 1,
        maximalMonth: +selectedYear! === CURRENT_YEAR ? CURRENT_MONTH_INDEX : 12,
      }),
    [i18n.language, props.minimalYear, props.minimalMonth, selectedYear],
  );
  const [selectedMonth, setSelectedMonth] = useState<string | undefined>(monthsList.find((month) => month.label === currentMonth)?.value);

  useEffect(() => {
    if (!currency) return;
    $monthlyTransactionStore.events.getPaymentSummary({
      Currency: currency,
      DateFrom: DateUtils.getFirstDateOfYear(selectedYear, selectedMonth),
      DateTo: DateUtils.getLastDateOfYear(selectedYear, selectedMonth),
    });
    return () => $monthlyTransactionStore.events.clear();
  }, [currency, selectedYear, selectedMonth]);

  const tabs = useMemo(
    () => [
      { label: t('payment.tabs.outgoingTransactions'), value: Tab.Outgoing },
      { label: t('payment.tabs.incomingTransactions'), value: Tab.Incoming },
    ],
    [t],
  );
  const content = useMemo(() => {
    if (!payment.data) return null;
    switch (tab) {
      case Tab.Outgoing:
        return (
          <TransactionItem
            {...payment.data.outgoingTransactions}
            itemType={TransactionDirection.Outgoing}
            currency={currency}
            textValue={`${monthsList.find((month) => month.value === selectedMonth)?.label} ${selectedYear}`}
          />
        );
      case Tab.Incoming:
        return (
          <TransactionItem
            {...payment.data.incomingTransactions}
            itemType={TransactionDirection.Incoming}
            currency={currency}
            textValue={`${monthsList.find((month) => month.value === selectedMonth)?.label} ${selectedYear}`}
          />
        );
      default: {
        const never: never = tab;
        return never;
      }
    }
  }, [tab, currency, monthsList, payment.data]);
  return (
    <div
      className={cn(
        PaletteUtils.isInsigniaPalette && 'flex flex-col gap-6',
        PaletteUtils.isVcfcPalette && 'rounded-xl bg-white dark:border dark:border-gray-black dark:bg-soft-black',
      )}
    >
      <div
        className={cn(
          'flex items-center justify-between gap-6',
          'max-md:flex-col max-md:items-start max-md:gap-3',
          PaletteUtils.isVcfcPalette && ['p-6 pb-0', 'max-md:p-4'],
        )}
      >
        <Text weight={PaletteUtils.isVcfcPalette ? 'semibold' : undefined} size={PaletteUtils.isVcfcPalette ? 'lg' : '2xl'}>
          {t('payment.monthlyTransaction.title')}
        </Text>
        <div className={cn('flex gap-6', 'max-md:w-full max-md:flex-col max-md:gap-3')}>
          <Select className='min-w-[100px]' items={currenciesAvailableStore.data} value={currency} onChange={(value) => setCurrency(value!)} />
          <Select className='min-w-[130px]' items={monthsList} value={selectedMonth} onChange={setSelectedMonth} />
          <Select items={yearsList} value={selectedYear} onChange={(value) => setSelectedYear(value!)} />
        </div>
      </div>
      <View
        loadingClassName={cn(PaletteUtils.isVcfcPalette && 'pb-6 pt-4')}
        errorClassName={cn(PaletteUtils.isVcfcPalette && 'p-6 pt-4')}
        noDataClassName={cn(PaletteUtils.isVcfcPalette && 'p-6 pt-4')}
        loading={payment.loading}
        error={payment.error}
        onRefresh={() =>
          $monthlyTransactionStore.events.getPaymentSummary({
            Currency: currency,
            DateFrom: DateUtils.getFirstDateOfYear(selectedYear, selectedMonth),
            DateTo: DateUtils.getLastDateOfYear(selectedYear, selectedMonth),
          })
        }
      >
        <Tabs className={cn(PaletteUtils.isVcfcPalette && ['w-full p-6', 'max-md:p-4'])} options={tabs} value={tab} onChange={setTab} />
        {content}
      </View>
    </div>
  );
};
